import React, { useState, useMemo, useCallback } from "react";
import clsx from "clsx";
import type { UploadedFile } from "../../../store/fileUploadStore";
import { getFileIcon, getFileTypeLabel } from "../FileTypeUtils/fileTypeUtils";
import { FilePreviewModal } from "../../molecules/FilePreviewModal";
import type { PreviewFile } from "../../molecules/FilePreviewModal";
import { Tooltip } from "react-tooltip";

interface AttachedFilesListProps {
  files: UploadedFile[];
  onRemove: (id: string) => void;
  isInputDisabled?: boolean;
  replyMessage: any;
}

const AttachedFilesList: React.FC<AttachedFilesListProps> = ({
  files,
  onRemove,
  isInputDisabled = false,
  replyMessage
}) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewInitialIndex, setPreviewInitialIndex] = useState(0);

  // Memoize preview files to prevent unnecessary re-renders of FilePreviewModal
  // This ensures a stable reference as long as the files array content doesn't change
  const previewFiles = useMemo((): PreviewFile[] => {
    return files
      .filter((file) => file.uploadStatus === "completed" && file.fileUrl)
      .map((file) => ({
        url: file.fileUrl!,
        name: file.name,
        type: file.type,
      }));
  }, [files]);

  // Memoize onClose to prevent unnecessary re-renders
  const handleClosePreview = useCallback(() => {
    setIsPreviewOpen(false);
  }, []);

  const handleFileClick = useCallback((clickedFile: UploadedFile) => {
    // Only allow preview for completed uploads with URLs
    if (clickedFile.uploadStatus !== "completed" || !clickedFile.fileUrl) return;
    
    const index = previewFiles.findIndex((f) => f.url === clickedFile.fileUrl);
    
    if (index !== -1) {
      setPreviewInitialIndex(index);
      setIsPreviewOpen(true);
    }
  }, [previewFiles]);

  // Early return AFTER all hooks have been called
  if (files.length === 0) return null;

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };  

  return (
    <>
      <div className="flex flex-wrap gap-2" style={{ marginBottom: replyMessage && "43px" }}>
        {files.map((file) => (
          <div
            key={file.id}
  data-tooltip-id={file.uploadStatus === "error" ? "error-tooltip-button" : undefined} // Show tooltip only when error

            className={`flex items-center gap-3 px-2 py-1.5 rounded-md 
             ${file.uploadStatus === "error" 
              ? "border !border-red-300 cursor-pointer" 
              : "bg-white border border-gray-300"} 
            ${file.uploadStatus === "completed" && file.fileUrl ? "cursor-pointer hover:bg-gray-50 hover:border-gray-400 transition-colors" : ""}
            `}

            onClick={() => handleFileClick(file)}
            role={file.uploadStatus === "completed" && file.fileUrl ? "button" : undefined}
            tabIndex={file.uploadStatus === "completed" && file.fileUrl ? 0 : undefined}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && file.uploadStatus === "completed" && file.fileUrl) {
                e.preventDefault();
                handleFileClick(file);
              }
            }}
          >
            {/* File Icon */}
            <div className="min-w-0">
              {getFileIcon(file.type, "w-6 h-6")}
            </div>

            {/* File Info */}
            <div className="flex-shrink-0 flex flex-col items-start">
              <div className="text-xs font-medium text-gray-900 truncate max-w-[100px]">
                {file.name}
              </div>
              <span className="text-[10px] text-gray-600 mt-0.5"> {getFileTypeLabel(file.name)}</span>
            </div>

            {/* Loading Indicator or Remove Button */}
            {file.uploadStatus === "uploading" ? (
              <div className="flex-shrink-0">
                <svg
                  className="animate-spin h-4 w-4 text-gray-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
              </div>
            ) : file.uploadStatus === "error" ? (
              <div className ="display: flex">
                <button
                  onClick={(e) => {
                    e.stopPropagation();

                    onRemove(file.id);
                  }}
                  className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors ml-1"
                  aria-label="Remove file"
                  type="button"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18 6L6 18M6 6L18 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                {
                  file.uploadStatus === "error"  && 
                  <Tooltip
                  id="error-tooltip-button"
                  place="top"
                  content={file.error || "Error uploading file"}
                  />
                }
              </div>                       
            ) : (
              !isInputDisabled && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(file.id);
                  }}
                  className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors ml-1"
                  aria-label="Remove file"
                  type="button"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18 6L6 18M6 6L18 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )
            )}
          </div>
        ))}
      </div>

      {/* File Preview Modal */}
      <FilePreviewModal
        isOpen={isPreviewOpen}
        files={previewFiles}
        initialIndex={previewInitialIndex}
        onClose={handleClosePreview}
      />
    </>
  );
};

export default AttachedFilesList;

